import { cn } from "@omni/utils/styles";
import { MouseEvent, useId } from "react";
import { useSelector } from "react-redux";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { set as setProductComparisonSkuList } from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.module";
import { getProductComparisonSkuList } from "@/redux/modules/value/productComparisonSkuList/productComparisonSkuList.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

type CompareProps = {
    sku: string;
    isAdditionalService: boolean;
    isBoxArticle: boolean;
    className?: string;
};

const Compare = ({
    sku,
    isAdditionalService,
    isBoxArticle,
    className
}: CompareProps) => {
    const { t } = useGranite();
    const isClient = useIsClientSide();
    const dispatch = useThunkDispatch();

    const productComparisonSkuList =
        useSelector(getProductComparisonSkuList) || [];

    const maxProductsInComparisonReached =
        productComparisonSkuList.length === 6 &&
        !productComparisonSkuList.includes(sku);

    const disabled = !isClient || maxProductsInComparisonReached;

    const id = useId();

    const onSetProductComparisonSkuListInternal = (skuList: string[]) => {
        dispatch(setProductComparisonSkuList(skuList));
    };

    const handleOnClick = (event: MouseEvent) => {
        event.stopPropagation();
    };

    if (isAdditionalService || isBoxArticle) {
        return null;
    }

    return (
        <FormGroup
            toggle={true}
            onClick={handleOnClick}
            className={cn(className)}
            disabled={disabled}
        >
            <Label
                htmlFor={id}
                className={cn("tw:cursor-pointer")}
            >
                <Input
                    id={id}
                    type="checkbox"
                    disabled={disabled}
                    checked={productComparisonSkuList.includes(sku)}
                    onChange={(event) => {
                        if ((event.target as HTMLInputElement).checked) {
                            return onSetProductComparisonSkuListInternal([
                                ...productComparisonSkuList,
                                sku
                            ]);
                        }

                        return onSetProductComparisonSkuListInternal(
                            productComparisonSkuList.filter(
                                (pcSku) => pcSku !== sku
                            )
                        );
                    }}
                />
                <span
                    className={cn("tw:select-none", {
                        "tw:text-muted-foreground": disabled
                    })}
                >
                    {t(
                        maxProductsInComparisonReached
                            ? "productComparison.maxAmountSelected"
                            : "productComparison.compareProduct"
                    )}
                </span>
            </Label>
        </FormGroup>
    );
};

export { Compare };
